<script setup lang="ts">
import { format, parseISO, isBefore, startOfDay, isSameDay } from 'date-fns'

const speedDialStore = useSpeedDialStore()
const prestacaoStore = usePrestacaoDeContaStore()
const permissaoStore = usePermissaoStore()
const usuarioStore = useUsuarioStore()
const { modalDespesasFiltradas } = storeToRefs(speedDialStore)

const formatarData = (data: string) => {
  return format(parseISO(data), 'dd/MM/yyyy')
}

const formatarValor = (valor: number | string) => {
  const valorNumerico = typeof valor === 'string' ? parseFloat(valor.replace(',', '.')) : valor
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(valorNumerico)
}

const getStatusClass = (vencimento: string) => {
  const dataVencimento = parseISO(vencimento)
  const hoje = new Date()

  if (isSameDay(dataVencimento, hoje)) {
    return 'text-warning fw-semibold' // Vence hoje
  } else if (isBefore(dataVencimento, startOfDay(hoje))) {
    return 'text-danger fw-semibold' // Vencida
  } else {
    return 'text-info' // Próxima
  }
}

const getStatusTexto = (vencimento: string) => {
  const dataVencimento = parseISO(vencimento)
  const hoje = new Date()

  if (isSameDay(dataVencimento, hoje)) {
    return 'Vence hoje'
  } else if (isBefore(dataVencimento, startOfDay(hoje))) {
    return 'Vencida'
  } else {
    return 'Próxima'
  }
}

const abrirDetalhes = (despesa: any) => {
  speedDialStore.abrirDespesa(despesa.id)
}

const editarDespesa = (despesa: any) => {
  speedDialStore.editarDespesa(despesa)
}

const pagarDespesa = (despesa: any) => {
  speedDialStore.pagarDespesa(despesa)
}

const stripHtml = (html: string): string => {
  if (!html) { return '' }
  // Remove todas as tags HTML usando regex
  return html.replace(/<[^>]*>/g, '').trim()
}

// Verifica se a despesa é de competência fechada (mês anterior)
const isDespesaCompetenciaFechada = (vencimento: string): boolean => {
  try {
    const hoje = new Date()
    const dataVencimento = parseISO(vencimento)

    // Criar data da competência da despesa (primeiro dia do mês)
    const competenciaDespesa = new Date(dataVencimento.getFullYear(), dataVencimento.getMonth(), 1)
    const competenciaAtual = new Date(hoje.getFullYear(), hoje.getMonth(), 1)

    // Competência é fechada se for anterior à atual
    return isBefore(competenciaDespesa, competenciaAtual)
  } catch (error) {
    console.warn('Erro ao verificar competência da despesa:', error)
    return false // Em caso de erro, considera como aberta
  }
}

// Verifica se usuário pode editar esta despesa específica
const podeEditarDespesa = (despesa: any): boolean => {
  try {
    // Verificação base de permissão
    const temPermissaoBase = usuarioStore.usuarioAtual.cartorio.permissoes
      .some(permissao => permissao.editar === 1 && permissao.especialidade_nome === 'financeiro')

    if (!temPermissaoBase) { return false }

    // Admin sempre pode editar
    const isAdmin = usuarioStore.usuarioAtual.usuario?.admin === 'S'
    if (isAdmin) { return true }

    // Usuários com permissão de exclusão não são afetados pela trava
    const temPermissaoExcluir = usuarioStore.usuarioAtual.cartorio.permissoes
      .some(permissao => permissao.excluir === 1 && permissao.especialidade_nome === 'financeiro')

    if (temPermissaoExcluir) { return true }

    // Para outros usuários, verificar se a competência da despesa está fechada
    const competenciaFechada = isDespesaCompetenciaFechada(despesa.vencimento)

    // Se trava desativada, pode editar
    if (permissaoStore.permitirEdicaoCompetenciaFechada) { return true }

    // Se trava ativada e competência fechada, não pode editar
    if (competenciaFechada) { return false }

    return true
  } catch (error) {
    console.warn('Erro na verificação de permissão editar despesa:', error)
    return false
  }
}

// Verifica se usuário pode excluir esta despesa específica
const podeExcluirDespesa = (): boolean => {
  try {
    // Verificação base de permissão
    const temPermissaoBase = usuarioStore.usuarioAtual.cartorio.permissoes
      .some(permissao => permissao.excluir === 1 && permissao.especialidade_nome === 'financeiro')

    if (!temPermissaoBase) { return false }

    // Admin sempre pode excluir
    const isAdmin = usuarioStore.usuarioAtual.usuario?.admin === 'S'
    if (isAdmin) { return true }

    // Usuários com permissão de exclusão não são afetados pela trava
    if (temPermissaoBase) { return true }

    return false
  } catch (error) {
    console.warn('Erro na verificação de permissão excluir despesa:', error)
    return false
  }
}

// Verifica se usuário pode pagar esta despesa específica
const podePagarDespesa = (despesa: any): boolean => {
  try {
    // Não pode pagar se já foi paga
    if (despesa.data_pagamento) { return false }

    // Verificação base de permissão
    const temPermissaoBase = usuarioStore.usuarioAtual.cartorio.permissoes
      .some(permissao => permissao.editar === 1 && permissao.especialidade_nome === 'financeiro')

    if (!temPermissaoBase) { return false }

    // Admin sempre pode pagar
    const isAdmin = usuarioStore.usuarioAtual.usuario?.admin === 'S'
    if (isAdmin) { return true }

    // Usuários com permissão de exclusão não são afetados pela trava
    const temPermissaoExcluir = usuarioStore.usuarioAtual.cartorio.permissoes
      .some(permissao => permissao.excluir === 1 && permissao.especialidade_nome === 'financeiro')

    if (temPermissaoExcluir) { return true }

    // Para outros usuários, verificar se a competência da despesa está fechada
    const competenciaFechada = isDespesaCompetenciaFechada(despesa.vencimento)

    // Se trava desativada, pode pagar
    if (permissaoStore.permitirEdicaoCompetenciaFechada) { return true }

    // Se trava ativada e competência fechada, não pode pagar
    if (competenciaFechada) { return false }

    return true
  } catch (error) {
    console.warn('Erro na verificação de permissão pagar despesa:', error)
    return false
  }
}

const confirmarExclusao = (despesa: any) => {
  prestacaoStore.modalConfirmarExclusao = {
    exibir: true,
    id: despesa.id,
    mensagem: 'Deseja realmente excluir a despesa?',
  }
}
</script>

<template>
  <CModal
    :visible="modalDespesasFiltradas.exibir"
    size="xl"
    backdrop="static"
    @close="speedDialStore.fecharModal()"
  >
    <CModalHeader>
      <CModalTitle>
        <font-awesome-icon icon="fas fa-search" class="me-2" />
        {{ modalDespesasFiltradas.titulo }}
      </CModalTitle>
    </CModalHeader>

    <CModalBody>
      <!-- Filtros -->
      <CRow class="mb-4">
        <CCol>
          <CCard class="border-0 bg-light">
            <CCardBody class="py-3">
              <div class="d-flex justify-content-center">
                <CButtonGroup role="group" aria-label="Filtros de despesas">
                  <CButton
                    :color="modalDespesasFiltradas.filtroAtivo === 'vencidas' ? 'danger' : 'outline-danger'"
                    size="sm"
                    @click="speedDialStore.alterarFiltro('vencidas')"
                  >
                    <font-awesome-icon icon="fas fa-exclamation-triangle" class="me-1" />
                    Vencidas
                  </CButton>
                  <CButton
                    :color="modalDespesasFiltradas.filtroAtivo === 'hoje' ? 'warning' : 'outline-warning'"
                    size="sm"
                    @click="speedDialStore.alterarFiltro('hoje')"
                  >
                    <font-awesome-icon icon="fas fa-calendar" class="me-1" />
                    Hoje
                  </CButton>
                  <CButton
                    :color="modalDespesasFiltradas.filtroAtivo === 'proximas' ? 'info' : 'outline-info'"
                    size="sm"
                    @click="speedDialStore.alterarFiltro('proximas')"
                  >
                    <font-awesome-icon icon="fas fa-clock" class="me-1" />
                    Próximas (5 dias)
                  </CButton>
                </CButtonGroup>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <div v-if="modalDespesasFiltradas.loading" class="text-center py-4">
        <CSpinner size="lg" />
        <p class="mt-2">
          Carregando despesas...
        </p>
      </div>

      <div v-else>
        <!-- Resumo -->
        <CRow class="mb-4">
          <CCol md="6">
            <CCard class="h-100 border-0 bg-light">
              <CCardBody class="text-center">
                <div class="fs-3 text-primary mb-1">
                  {{ speedDialStore.quantidadeDespesasFiltradas }}
                </div>
                <div class="text-muted">
                  despesas encontradas
                </div>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md="6">
            <CCard class="h-100 border-0 bg-light">
              <CCardBody class="text-center">
                <div class="fs-3 mb-1 text-danger">
                  {{ formatarValor(speedDialStore.valorTotalDespesas) }}
                </div>
                <div class="text-muted">
                  valor total
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <!-- Tabela de despesas -->
        <div v-if="modalDespesasFiltradas.despesas.length === 0" class="text-center py-5">
          <font-awesome-icon icon="fas fa-check-circle" size="3x" class="text-success mb-3" />
          <h5>Nenhuma despesa encontrada!</h5>
          <p class="text-muted">
            <span v-if="modalDespesasFiltradas.filtroAtivo === 'vencidas'">
              Não há despesas vencidas no momento.
            </span>
            <span v-else-if="modalDespesasFiltradas.filtroAtivo === 'hoje'">
              Não há despesas vencendo hoje.
            </span>
            <span v-else-if="modalDespesasFiltradas.filtroAtivo === 'proximas'">
              Não há despesas próximas ao vencimento.
            </span>
            <span v-else>
              Nenhuma despesa encontrada para o filtro selecionado.
            </span>
          </p>
        </div>

        <PDataTable
          v-else
          :value="modalDespesasFiltradas.despesas"
          :paginator="true"
          :rows="10"
          :rows-per-page-options="[5, 10, 20]"
          paginator-template="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          current-page-report-template="{first} a {last} de {totalRecords}"
          responsive-layout="scroll"
          class="p-datatable-sm"
        >
          <PColumn field="descricao" header="Descrição" sortable>
            <template #body="{ data }">
              <div>
                <div class="fs-6">
                  {{ stripHtml(data.descricao) }}
                </div>
                <small class="text-muted">{{ data.conta.nome }}</small>
              </div>
            </template>
          </PColumn>

          <PColumn field="valor" header="Valor" sortable>
            <template #body="{ data }">
              <span class="fs-6 text-danger">{{ formatarValor(data.valor) }}</span>
            </template>
          </PColumn>

          <PColumn field="vencimento" header="Vencimento" sortable>
            <template #body="{ data }">
              <div :class="getStatusClass(data.vencimento)">
                {{ formatarData(data.vencimento) }}
                <br>
                <small>{{ getStatusTexto(data.vencimento) }}</small>
              </div>
            </template>
          </PColumn>

          <PColumn header="Ações" style="width: 200px">
            <template #body="{ data }">
              <div class="d-flex gap-1">
                <CButton
                  color="primary"
                  class="me-1"
                  @click="abrirDetalhes(data)"
                >
                  <CIcon icon="cilZoom" />
                </CButton>

                <CButton
                  v-if="podeEditarDespesa(data)"
                  color="warning"
                  class="me-1"
                  @click="editarDespesa(data)"
                >
                  <CIcon icon="cilPen" />
                </CButton>

                <CButton
                  v-if="podeExcluirDespesa"
                  color="danger"
                  class="me-1"
                  @click="confirmarExclusao(data)"
                >
                  <CIcon icon="cilTrash" />
                </CButton>

                <CButton
                  v-if="podePagarDespesa(data)"
                  v-c-tooltip="'Efetuar pagamento'"
                  color="success"
                  title="Pagar"
                  @click="pagarDespesa(data)"
                >
                  <CIcon icon="cilDollar" />
                </CButton>
              </div>
            </template>
          </PColumn>
        </PDataTable>
      </div>
    </CModalBody>

    <CModalFooter>
      <div class="d-flex justify-content-between w-100">
        <div>
          <CButton
            color="primary"
            variant="outline"
            :disabled="modalDespesasFiltradas.loading"
            @click="speedDialStore.buscarDespesasFiltradas(modalDespesasFiltradas.filtroAtivo as any)"
          >
            <font-awesome-icon icon="fas fa-sync-alt" />
            Atualizar
          </CButton>
        </div>

        <CButton
          color="secondary"
          @click="speedDialStore.fecharModal()"
        >
          Fechar
        </CButton>
      </div>
    </CModalFooter>
  </CModal>
</template>

<style scoped>
:deep(.p-datatable .p-datatable-thead > tr > th) {
  background-color: var(--cui-gray-100);
  border-bottom: 2px solid var(--cui-border-color);
  font-weight: 600;
}

:deep(.p-datatable .p-datatable-tbody > tr:hover) {
  background-color: var(--cui-gray-50);
}

:deep(.p-paginator) {
  background: var(--cui-gray-50);
  border-top: 1px solid var(--cui-border-color);
  padding: 0.75rem;
}
</style>
